import { useState, useMemo, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { usePageTitle } from '@/hooks/usePageTitle';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { useAllRequests, useRequestActions } from '@/hooks/useAllRequests';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  Calendar,
  Search,
  CheckCircle,
  XCircle,
  User,
  Clock,
  FileText,
  ListFilter,
  CheckSquare,
  Ban,
  Inbox
} from 'lucide-react';
import { toast } from 'sonner';
import { getRequestTypeLabel, getRequestStatusLabel } from '@/lib/attendanceTypes';
import { Pagination } from '@/components/Pagination';
import { cn } from '@/lib/utils';

// Consistent animation config
const EASE = [0.25, 0.46, 0.45, 0.94];

// Module-level constants
const LEAVE_TYPE_OPTIONS = [
  { value: 'all', label: 'All Types' },
  { value: 'annual_leave', label: 'Annual Leave' },
  { value: 'sick_leave', label: 'Sick Leave' },
  { value: 'unpaid_leave', label: 'Unpaid Leave' },
  { value: 'work_from_home', label: 'Work From Home' },
  { value: 'compassionate_leave', label: 'Compassionate Leave' },
  { value: 'parental_leave', label: 'Parental Leave' },
  { value: 'training', label: 'Training' }
];

// StatPill component matching other attendance pages
const StatPill = ({ icon: Icon, label, value, color = "slate", delay = 0 }) => {
  const colorClasses = {
    slate: "bg-slate-50 text-slate-700 border-slate-200",
    amber: "bg-amber-50 text-amber-700 border-amber-200",
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
    violet: "bg-violet-50 text-violet-700 border-violet-200",
    rose: "bg-rose-50 text-rose-700 border-rose-200",
    sky: "bg-sky-50 text-sky-700 border-sky-200"
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay, ease: EASE }}
      className={`flex items-center gap-3 px-4 py-3 rounded-2xl border ${colorClasses[color]} transition-all duration-300 hover:scale-[1.02] hover:shadow-md cursor-default`}
    >
      <div className="p-2 rounded-xl bg-white/80 shadow-sm">
        <Icon className="h-4 w-4" />
      </div>
      <div>
        <p className="text-xs font-medium opacity-70">{label}</p>
        <p className="text-lg font-bold -mt-0.5">{value}</p>
      </div>
    </motion.div>
  );
};

// Status tab button component
const StatusTab = ({ active, onClick, label, count, color }) => {
  const colorClasses = {
    amber: active ? "bg-amber-500 text-white border-amber-500" : "bg-white text-amber-700 border-amber-200 hover:bg-amber-50",
    emerald: active ? "bg-emerald-500 text-white border-emerald-500" : "bg-white text-emerald-700 border-emerald-200 hover:bg-emerald-50",
    rose: active ? "bg-rose-500 text-white border-rose-500" : "bg-white text-rose-700 border-rose-200 hover:bg-rose-50",
    slate: active ? "bg-slate-500 text-white border-slate-500" : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50",
    violet: active ? "bg-violet-500 text-white border-violet-500" : "bg-white text-violet-700 border-violet-200 hover:bg-violet-50"
  };

  return (
    <button
      onClick={onClick}
      className={cn(
        "px-4 py-2.5 rounded-full text-sm font-semibold border transition-all duration-200 flex items-center gap-2",
        colorClasses[color]
      )}
    >
      {label}
      {count > 0 && (
        <span className={cn(
          "px-1.5 py-0.5 rounded-full text-xs font-bold min-w-[20px] text-center",
          active ? "bg-white/20" : "bg-current/10"
        )}>
          {count}
        </span>
      )}
    </button>
  );
};

// Get status accent color
const getStatusAccent = (status) => {
  const accents = {
    pending: 'bg-amber-500',
    approved: 'bg-emerald-500',
    rejected: 'bg-rose-500',
    withdrawn: 'bg-slate-400'
  };
  return accents[status] || accents.pending;
};

// Get status badge style
const getStatusBadgeStyle = (status) => {
  const styles = {
    pending: "bg-amber-50 text-amber-700 border-amber-200",
    approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
    rejected: "bg-rose-50 text-rose-700 border-rose-200",
    withdrawn: "bg-slate-50 text-slate-600 border-slate-200"
  };
  return styles[status] || styles.pending;
};

// Request card component
const RequestCard = ({
  request,
  formatDate,
  getPartDayText,
  canManageAttendance,
  userProfile,
  showCheckbox,
  isSelected,
  onToggleSelect,
  onAction
}) => {
  const isPending = request.status === 'pending';
  const isApproved = request.status === 'approved';
  const accentColor = getStatusAccent(request.status);

  return (
    <div
      className="group relative bg-white rounded-xl border border-slate-200/80 p-4 pl-5 transition-all duration-300 hover:shadow-[0_4px_20px_-4px_rgba(0,0,0,0.1)] hover:border-slate-300/80"
    >
      {/* Status accent bar */}
      <div className={`absolute left-0 top-3 bottom-3 w-1 rounded-full ${accentColor}`} />

      <div className="flex items-start gap-4">
        {/* Checkbox for bulk selection */}
        {showCheckbox && (
          <div className="flex items-center pt-1">
            <Checkbox
              checked={isSelected}
              onCheckedChange={onToggleSelect}
              className="border-slate-300"
            />
          </div>
        )}

        {/* Content */}
        <div className="flex-1 min-w-0">
          {/* Header row */}
          <div className="flex items-center gap-2 flex-wrap mb-2">
            {request.user_profiles && (
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-full bg-slate-800 flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-semibold text-[10px]">
                    {request.user_profiles.first_name[0]}{request.user_profiles.last_name[0]}
                  </span>
                </div>
                <span className="text-[15px] font-semibold text-slate-800">
                  {request.user_profiles.first_name} {request.user_profiles.last_name}
                </span>
              </div>
            )}
            <span className={cn(
              "px-2.5 py-1 rounded-full text-xs font-semibold border",
              getStatusBadgeStyle(request.status)
            )}>
              {getRequestStatusLabel(request.status)}
            </span>
          </div>

          {/* Leave type and dates */}
          <div className="flex items-center gap-3 text-sm text-slate-600 mb-1">
            <span className="font-medium text-slate-700">{getRequestTypeLabel(request.leave_type)}</span>
            <span className="text-slate-300">•</span>
            <span>{formatDate(request.start_date)} - {formatDate(request.end_date)}</span>
          </div>

          {/* Part day info */}
          {getPartDayText(request) && (
            <p className="text-xs text-slate-500 mb-1">
              Part days: {getPartDayText(request)}
            </p>
          )}

          {/* User note */}
          {request.user_note && (request.user_id === userProfile?.id || canManageAttendance) && (
            <div className="mt-2 p-2.5 bg-slate-50 rounded-lg border border-slate-100">
              <div className="flex items-start gap-2 text-sm text-slate-600">
                <FileText className="h-4 w-4 mt-0.5 flex-shrink-0 text-slate-400" />
                <span>{request.user_note}</span>
              </div>
            </div>
          )}

          {/* Admin note (rejection reason) */}
          {request.admin_note && (
            <div className="mt-2 p-2.5 bg-rose-50 rounded-lg border border-rose-100">
              <div className="flex items-start gap-2 text-sm text-rose-700">
                <FileText className="h-4 w-4 mt-0.5 flex-shrink-0 text-rose-500" />
                <span><strong>Reason:</strong> {request.admin_note}</span>
              </div>
            </div>
          )}

          {/* Submitted date */}
          <p className="text-xs text-slate-400 mt-2">
            Submitted {formatDate(request.created_date)}
          </p>
        </div>

        {/* Actions */}
        <div className="flex flex-col gap-2 flex-shrink-0">
          {isPending && (
            <>
              <button
                onClick={() => onAction(request, 'approve')}
                className="px-3 py-1.5 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100 transition-colors flex items-center gap-1.5"
              >
                <CheckCircle className="h-3.5 w-3.5" />
                Approve
              </button>
              <button
                onClick={() => onAction(request, 'reject')}
                className="px-3 py-1.5 rounded-full text-xs font-semibold bg-rose-50 text-rose-700 border border-rose-200 hover:bg-rose-100 transition-colors flex items-center gap-1.5"
              >
                <XCircle className="h-3.5 w-3.5" />
                Reject
              </button>
            </>
          )}
          {isApproved && (
            <button
              onClick={() => onAction(request, 'withdraw')}
              className="px-3 py-1.5 rounded-full text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100 transition-colors flex items-center gap-1.5"
            >
              <Ban className="h-3.5 w-3.5" />
              Withdraw
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export const AllRequestsPage = () => {
  const navigate = useNavigate();
  const { userProfile, can, authReady } = useAuth();

  usePageTitle('All Leave Requests');
  const canManageAttendance = can('attendance.manage');

  // SECURITY: Redirect users without attendance management permission
  useEffect(() => {
    if (authReady && !canManageAttendance) {
      toast.error('You do not have permission to view all requests');
      navigate('/attendance');
    }
  }, [authReady, canManageAttendance, navigate]);

  // Dialog state
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [actionType, setActionType] = useState(null);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [selectedRequests, setSelectedRequests] = useState([]);
  const [bulkWithdrawDialogOpen, setBulkWithdrawDialogOpen] = useState(false);

  const {
    requests,
    stats,
    loading,
    pagination,
    filters,
    refetch
  } = useAllRequests(userProfile?.company_id);

  const { approveRequest, rejectRequest, withdrawRequest, bulkWithdraw, processing } = useRequestActions(() => {
    refetch();
    setSelectedRequests([]);
  });

  const formatDate = useCallback((dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  }, []);

  const getPartDayText = useCallback((request) => {
    if (!request.part_day) return null;
    const parts = [];
    if (request.part_day.start) parts.push(`Start: ${request.part_day.start}`);
    if (request.part_day.end) parts.push(`End: ${request.part_day.end}`);
    return parts.join(', ');
  }, []);

  const openActionDialog = useCallback((request, type) => {
    setSelectedRequest(request);
    setActionType(type);
    setRejectionReason('');
    setActionDialogOpen(true);
  }, []);

  const handleAction = useCallback(async () => {
    if (!selectedRequest || !actionType) return;

    if (actionType === 'reject' && !rejectionReason.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }

    let success = false;

    if (actionType === 'approve') {
      success = await approveRequest(selectedRequest, userProfile);
    } else if (actionType === 'reject') {
      success = await rejectRequest(selectedRequest, rejectionReason.trim(), userProfile);
    } else if (actionType === 'withdraw') {
      success = await withdrawRequest(selectedRequest);
    }

    if (success) {
      try {
        await sendActionNotification(selectedRequest, actionType, userProfile, rejectionReason);
      } catch (notifError) {
        console.error('Failed to send notification:', notifError);
      }

      const actionLabel = actionType === 'approve' ? 'approved' : actionType === 'reject' ? 'rejected' : 'withdrawn';
      toast.success(`Request ${actionLabel} successfully`);
      setActionDialogOpen(false);
      setSelectedRequest(null);
      setActionType(null);
      setRejectionReason('');
    } else {
      toast.error(`Failed to ${actionType} request`);
    }
  }, [selectedRequest, actionType, rejectionReason, approveRequest, rejectRequest, withdrawRequest, userProfile]);

  const handleBulkWithdraw = useCallback(async () => {
    if (selectedRequests.length === 0) return;

    const success = await bulkWithdraw(selectedRequests);

    if (success) {
      toast.success(`${selectedRequests.length} request(s) withdrawn successfully`);
      setBulkWithdrawDialogOpen(false);
    } else {
      toast.error('Failed to withdraw requests');
    }
  }, [selectedRequests, bulkWithdraw]);

  const toggleSelectRequest = useCallback((requestId) => {
    setSelectedRequests(prev =>
      prev.includes(requestId)
        ? prev.filter(id => id !== requestId)
        : [...prev, requestId]
    );
  }, []);

  const approvedRequestsOnPage = useMemo(() => {
    return requests.filter(r => r.status === 'approved');
  }, [requests]);

  const toggleSelectAll = useCallback(() => {
    if (selectedRequests.length === approvedRequestsOnPage.length) {
      setSelectedRequests([]);
    } else {
      setSelectedRequests(approvedRequestsOnPage.map(r => r.id));
    }
  }, [selectedRequests.length, approvedRequestsOnPage]);

  const handleStatusTabChange = useCallback((value) => {
    filters.setStatus(value);
    setSelectedRequests([]);
  }, [filters]);

  const handleSearchChange = useCallback((e) => {
    filters.setSearch(e.target.value);
  }, [filters]);

  const handleTypeChange = useCallback((value) => {
    filters.setType(value);
  }, [filters]);

  const handlePageChange = useCallback((newPage) => {
    pagination.setPage(newPage);
    setSelectedRequests([]);
  }, [pagination]);

  // Loading state
  if (loading && requests.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-96 gap-4">
        <div className="relative">
          <div className="w-16 h-16 rounded-full border-4 border-slate-100" />
          <motion.div
            className="absolute inset-0 w-16 h-16 rounded-full border-4 border-transparent border-t-slate-500"
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
          />
        </div>
        <p className="text-sm text-slate-500 font-medium">Loading requests...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Hero Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: EASE }}
        className="rounded-2xl bg-white border border-slate-200 p-8"
      >
        <div className="flex items-start justify-between mb-6">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-3xl font-bold text-slate-800 tracking-tight">All Requests</h1>
              {stats.pending > 0 && (
                <span className="px-2.5 py-1 rounded-full text-sm font-bold bg-amber-500 text-white">
                  {stats.pending} pending
                </span>
              )}
            </div>
            <p className="text-slate-500 mt-1">Review and manage leave requests</p>
          </div>
          <div className="p-3 rounded-2xl bg-slate-50 border border-slate-100">
            <CheckSquare className="h-6 w-6 text-slate-600" />
          </div>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatPill
            icon={Inbox}
            label="Total Requests"
            value={stats.total}
            color="slate"
            delay={0.1}
          />
          <StatPill
            icon={Clock}
            label="Pending"
            value={stats.pending}
            color={stats.pending > 0 ? "amber" : "slate"}
            delay={0.15}
          />
          <StatPill
            icon={CheckCircle}
            label="Approved"
            value={stats.approved}
            color="emerald"
            delay={0.2}
          />
          <StatPill
            icon={XCircle}
            label="Rejected"
            value={stats.rejected}
            color={stats.rejected > 0 ? "rose" : "slate"}
            delay={0.25}
          />
        </div>
      </motion.div>

      {/* Status Tabs */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.1, ease: EASE }}
        className="flex flex-wrap gap-2"
      >
        <StatusTab
          active={filters.status === 'pending'}
          onClick={() => handleStatusTabChange('pending')}
          label="Pending"
          count={stats.pending}
          color="amber"
        />
        <StatusTab
          active={filters.status === 'approved'}
          onClick={() => handleStatusTabChange('approved')}
          label="Approved"
          count={stats.approved}
          color="emerald"
        />
        <StatusTab
          active={filters.status === 'rejected'}
          onClick={() => handleStatusTabChange('rejected')}
          label="Rejected"
          count={stats.rejected}
          color="rose"
        />
        <StatusTab
          active={filters.status === 'withdrawn'}
          onClick={() => handleStatusTabChange('withdrawn')}
          label="Withdrawn"
          count={stats.withdrawn}
          color="slate"
        />
        <StatusTab
          active={filters.status === 'all'}
          onClick={() => handleStatusTabChange('all')}
          label="All"
          count={stats.total}
          color="violet"
        />
      </motion.div>

      {/* Requests Section */}
      <div className="rounded-2xl bg-white border border-slate-200 p-6">
        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-3 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Search requests..."
              value={filters.search}
              onChange={handleSearchChange}
              className="pl-9 h-11 rounded-full border-slate-200 bg-slate-50 focus:bg-white transition-colors"
            />
          </div>
          <Select value={filters.type || 'all'} onValueChange={handleTypeChange}>
            <SelectTrigger className="w-[200px] rounded-full border-slate-200 bg-slate-50 hover:bg-slate-100 transition-colors">
              <ListFilter className="h-4 w-4 mr-2 text-slate-400" />
              <SelectValue placeholder="Filter by type" />
            </SelectTrigger>
            <SelectContent>
              {LEAVE_TYPE_OPTIONS.map(option => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Bulk Actions Bar */}
        <AnimatePresence>
          {filters.status === 'approved' && approvedRequestsOnPage.length > 0 && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="mb-4"
            >
              <div className="p-3 bg-sky-50 border border-sky-200 rounded-xl flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Checkbox
                    checked={selectedRequests.length === approvedRequestsOnPage.length && approvedRequestsOnPage.length > 0}
                    onCheckedChange={toggleSelectAll}
                    className="border-sky-300"
                  />
                  <span className="text-sm font-medium text-sky-700">
                    {selectedRequests.length > 0
                      ? `${selectedRequests.length} selected`
                      : 'Select all'}
                  </span>
                </div>
                {selectedRequests.length > 0 && (
                  <button
                    onClick={() => setBulkWithdrawDialogOpen(true)}
                    className="px-3 py-1.5 rounded-full text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100 transition-colors flex items-center gap-1.5"
                  >
                    <Ban className="h-3.5 w-3.5" />
                    Withdraw Selected ({selectedRequests.length})
                  </button>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Requests List */}
        {requests.length === 0 ? (
          <div className="text-center py-16">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-slate-50 border border-slate-100 mb-4">
              <Calendar className="h-6 w-6 text-slate-400" />
            </div>
            <p className="text-[15px] font-semibold text-slate-700">No requests found</p>
            <p className="text-sm text-slate-400 mt-1">Try adjusting your filters</p>
          </div>
        ) : (
          <div className="space-y-3">
            {requests.map((request) => {
              const isApproved = request.status === 'approved';
              const showCheckbox = filters.status === 'approved' && isApproved;

              return (
                <RequestCard
                  key={request.id}
                  request={request}
                  formatDate={formatDate}
                  getPartDayText={getPartDayText}
                  canManageAttendance={canManageAttendance}
                  userProfile={userProfile}
                  showCheckbox={showCheckbox}
                  isSelected={selectedRequests.includes(request.id)}
                  onToggleSelect={() => toggleSelectRequest(request.id)}
                  onAction={openActionDialog}
                />
              );
            })}
          </div>
        )}

        {/* Pagination */}
        {pagination.filteredItems > 0 && (
          <div className="mt-6 pt-6 border-t border-slate-100">
            <Pagination
              currentPage={pagination.page}
              totalPages={pagination.totalPages}
              totalItems={pagination.filteredItems}
              itemsPerPage={pagination.pageSize}
              onPageChange={handlePageChange}
              onItemsPerPageChange={pagination.setPageSize}
            />
          </div>
        )}
      </div>

      {/* Action Dialog */}
      <Dialog open={actionDialogOpen} onOpenChange={setActionDialogOpen}>
        <DialogContent className="sm:max-w-[500px] rounded-2xl border-slate-200">
          <DialogHeader className="pb-4 border-b border-slate-100">
            <DialogTitle className="text-xl font-bold text-slate-800">
              {actionType === 'approve' ? 'Approve' : actionType === 'reject' ? 'Reject' : 'Withdraw'} Request
            </DialogTitle>
            <DialogDescription className="text-slate-500">
              {actionType === 'approve'
                ? "This will approve the leave request and update the user's balance."
                : actionType === 'reject'
                ? 'The user will be notified of the rejection with your provided reason.'
                : "This will withdraw the leave request and update the user's balance if applicable."}
            </DialogDescription>
          </DialogHeader>

          {selectedRequest && (
            <div className="py-4">
              <div className="relative p-4 pl-5 bg-slate-50 rounded-xl border border-slate-100">
                <div className={`absolute left-0 top-3 bottom-3 w-1 rounded-full ${getStatusAccent(selectedRequest.status)}`} />
                <div className="flex items-center gap-2 mb-2">
                  {selectedRequest.user_profiles && (
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded-full bg-slate-800 flex items-center justify-center">
                        <span className="text-white font-semibold text-[9px]">
                          {selectedRequest.user_profiles.first_name[0]}{selectedRequest.user_profiles.last_name[0]}
                        </span>
                      </div>
                      <span className="text-sm font-semibold text-slate-800">
                        {selectedRequest.user_profiles.first_name} {selectedRequest.user_profiles.last_name}
                      </span>
                    </div>
                  )}
                </div>
                <p className="text-sm font-medium text-slate-700 mb-1">
                  {getRequestTypeLabel(selectedRequest.leave_type)}
                </p>
                <p className="text-sm text-slate-500">
                  {formatDate(selectedRequest.start_date)} - {formatDate(selectedRequest.end_date)}
                </p>
                {selectedRequest.user_note && (selectedRequest.user_id === userProfile?.id || canManageAttendance) && (
                  <p className="text-sm text-slate-500 mt-2">
                    <span className="font-medium">Note:</span> {selectedRequest.user_note}
                  </p>
                )}
              </div>
            </div>
          )}

          {actionType === 'reject' && (
            <div className="space-y-2 pb-4">
              <label className="text-sm font-medium text-slate-700">
                Rejection Reason <span className="text-rose-500">*</span>
              </label>
              <Textarea
                placeholder="Provide a reason for rejection..."
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                className="min-h-[100px] rounded-xl border-slate-200 focus:border-slate-300 focus:ring-slate-200 resize-none"
              />
            </div>
          )}

          <DialogFooter className="pt-4 border-t border-slate-100 gap-3">
            <Button
              variant="outline"
              onClick={() => setActionDialogOpen(false)}
              disabled={processing}
              className="rounded-full border-slate-200"
            >
              Cancel
            </Button>
            <Button
              onClick={handleAction}
              disabled={processing}
              className={cn(
                "rounded-full",
                actionType === 'approve' && "bg-emerald-600 hover:bg-emerald-700",
                actionType === 'reject' && "bg-rose-600 hover:bg-rose-700",
                actionType === 'withdraw' && "bg-amber-600 hover:bg-amber-700"
              )}
            >
              {processing ? (
                <>
                  <motion.div
                    className="w-4 h-4 rounded-full border-2 border-white/30 border-t-white mr-2"
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                  />
                  Processing...
                </>
              ) : (
                <>
                  {actionType === 'approve' && <CheckCircle className="h-4 w-4 mr-2" />}
                  {actionType === 'reject' && <XCircle className="h-4 w-4 mr-2" />}
                  {actionType === 'withdraw' && <Ban className="h-4 w-4 mr-2" />}
                  {actionType === 'approve' ? 'Approve' : actionType === 'reject' ? 'Reject' : 'Withdraw'} Request
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Withdraw Dialog */}
      <Dialog open={bulkWithdrawDialogOpen} onOpenChange={setBulkWithdrawDialogOpen}>
        <DialogContent className="sm:max-w-[425px] rounded-2xl border-slate-200">
          <DialogHeader className="pb-4 border-b border-slate-100">
            <DialogTitle className="text-xl font-bold text-slate-800">Withdraw Multiple Requests</DialogTitle>
            <DialogDescription className="text-slate-500">
              Are you sure you want to withdraw {selectedRequests.length} leave request(s)? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            <div className="p-4 rounded-xl bg-amber-50/50 border border-amber-200/50 text-center">
              <p className="text-2xl font-bold text-amber-700">{selectedRequests.length}</p>
              <p className="text-sm text-amber-600">requests will be withdrawn</p>
            </div>
          </div>

          <DialogFooter className="pt-4 border-t border-slate-100 gap-3">
            <Button
              variant="outline"
              onClick={() => setBulkWithdrawDialogOpen(false)}
              disabled={processing}
              className="rounded-full border-slate-200"
            >
              Cancel
            </Button>
            <Button
              onClick={handleBulkWithdraw}
              disabled={processing}
              className="rounded-full bg-amber-600 hover:bg-amber-700"
            >
              {processing ? (
                <>
                  <motion.div
                    className="w-4 h-4 rounded-full border-2 border-white/30 border-t-white mr-2"
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                  />
                  Withdrawing...
                </>
              ) : (
                <>
                  <Ban className="h-4 w-4 mr-2" />
                  Withdraw {selectedRequests.length} Request(s)
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

/**
 * Send notification for leave action
 */
async function sendActionNotification(request, actionType, userProfile, rejectionReason) {
  const employeeName = request.user_profiles
    ? `${request.user_profiles.first_name} ${request.user_profiles.last_name}`
    : 'Employee';

  const startDate = new Date(request.start_date).toLocaleDateString('en-GB', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
  });
  const endDate = new Date(request.end_date).toLocaleDateString('en-GB', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
  });

  let notificationTitle, notificationMessage, leaveType;

  if (actionType === 'approve') {
    leaveType = 'approved';
    notificationTitle = 'Leave Request Approved';
    notificationMessage = `Your ${request.leave_type} request has been approved`;
  } else if (actionType === 'reject') {
    leaveType = 'rejected';
    notificationTitle = 'Leave Request Not Approved';
    notificationMessage = `Your ${request.leave_type} request has not been approved`;
  } else {
    leaveType = 'withdrawn';
    notificationTitle = 'Leave Request Withdrawn';
    notificationMessage = `Your ${request.leave_type} request has been withdrawn`;
  }

  const daysDiff = Math.ceil((new Date(request.end_date) - new Date(request.start_date)) / (1000 * 60 * 60 * 24)) + 1;
  const firstName = request.user_profiles?.first_name || employeeName.split(' ')[0];

  let remainingBalance = null;
  if (actionType === 'withdraw') {
    const { data: balance } = await supabase
      .from('leave_balances')
      .select('remaining_days')
      .eq('user_id', request.user_id)
      .eq('year', new Date().getFullYear())
      .maybeSingle();

    remainingBalance = balance?.remaining_days || 0;
  }

  const currentDate = new Date().toLocaleDateString('en-GB', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
    hour: '2-digit', minute: '2-digit'
  });

  await supabase.functions.invoke('process-notification', {
    body: {
      type: 'leave',
      leave_type: leaveType,
      user_ids: [request.user_id],
      title: notificationTitle,
      message: notificationMessage,
      link_url: `/attendance/history`,
      entity_type: 'leave_request',
      entity_id: request.id,
      actor_id: userProfile.id,
      template_data: {
        employee_name: employeeName,
        first_name: firstName,
        leave_type: request.leave_type,
        start_date: startDate,
        end_date: endDate,
        total_days: daysDiff,
        approver_name: `${userProfile.first_name} ${userProfile.last_name}`,
        approved_date: actionType === 'approve' ? currentDate : undefined,
        rejection_reason: actionType === 'reject' ? rejectionReason : undefined,
        rejected_date: actionType === 'reject' ? currentDate : undefined,
        withdrawn_date: actionType === 'withdraw' ? currentDate : undefined,
        remaining_balance: actionType === 'withdraw' ? remainingBalance : undefined,
        app_url: window.location.origin,
        short_link: `${window.location.origin}/attendance/history`
      }
    }
  });
}
